import { Input } from '@globalmed/atlas-ui';
import { ChangeEvent, InputEvent, KeyboardEvent, useRef } from 'react';
import { TNullable } from '@/types/common';

type TProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  message?: string;
  isError?: boolean;
  required?: boolean;
  label?: string;
  'aria-label'?: string;
  thousandSeparator?: boolean;
};

/**
 * A numeric input component which only allows integer values to be entered.
 * Optionally allows negative numbers.
 * @param props - The component props.
 * @param props.value - The current value of the input.
 * @param props.onChange - Called when the value of the input changes.
 * @param props.placeholder - The placeholder text to display in the input.
 * @param props.width - The width of the input.
 * @param props.min - The minimum value allowed in the input.
 * @param props.max - The maximum value allowed in the input.
 * @param props.disabled - Whether the input is disabled.
 * @param props.message - The message to display below the input.
 * @param props.isError - Whether the input is in an error state.
 * @param props.required - Whether the input is a required field.
 * @param props.label - The label of the input.
 * @param props.'aria-label' - Accessible label for inputs without visible text.
 * @param props.thousandSeparator - When true, displays the value grouped by
 * thousands with commas (e.g. "1,500"), while `onChange` still reports the
 * plain digit string. The caret keeps its place among the digits as they are
 * regrouped, and deleting a separator deletes the digit beside it.
 * @returns A numeric input component.
 */
export const IntegerInput = ({
  value,
  onChange,
  placeholder,
  width,
  min,
  max,
  disabled = false,
  message,
  isError,
  required,
  label,
  'aria-label': ariaLabel,
  thousandSeparator = false,
}: TProps) => {
  const allowNegative = min === undefined || Number(min) < 0;
  // The atlas Input takes only a callback ref, and hands `onChange` a copy of
  // the input without its caret, so the caret is read from here.
  const inputRef = useRef<TNullable<HTMLInputElement>>(null);

  /**
   * Puts the caret after the given number of characters, separators aside,
   * once the regrouped value is shown: the atlas Input shows a new value a
   * render later, so this waits for the next frame.
   * @param caretChars - The digits (and minus sign) before the caret.
   * @returns The id of the scheduled frame.
   */
  const placeCaret = (caretChars: number) =>
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      let position = 0;
      for (
        let counted = 0;
        counted < caretChars && position < input.value.length;
        position++
      ) {
        if (input.value[position] !== ',') counted++;
      }
      input.setSelectionRange(position, position);
    });

  const replacePattern = allowNegative ? /[^0-9-]/g : /[^0-9]/g;
  const pattern = `[0-9${thousandSeparator ? ',' : ''}${allowNegative ? '-' : ''}]*`;
  const isNegative = value.startsWith('-');
  const digits = isNegative ? value.slice(1) : value;
  const displayValue =
    thousandSeparator && value
      ? `${isNegative ? '-' : ''}${digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
      : value;

  const filterValue = (text: string) =>
    text.replace(replacePattern, '').replace(/(?!^)-/g, '');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const filteredValue = filterValue(inputValue);
    const caretChars = filterValue(
      inputValue.slice(
        0,
        inputRef.current?.selectionStart ?? inputValue.length,
      ),
    ).length;
    const inputType =
      'inputType' in e.nativeEvent ? e.nativeEvent.inputType : undefined;

    // Deleting a separator alone changes no digit, so the digit beside it
    // goes instead.
    const isBackward = inputType === 'deleteContentBackward' && caretChars > 0;
    const isSeparatorDeleted =
      thousandSeparator &&
      filteredValue === value &&
      (isBackward || inputType === 'deleteContentForward');
    const nextCaretChars =
      isSeparatorDeleted && isBackward ? caretChars - 1 : caretChars;
    const nextValue = isSeparatorDeleted
      ? filteredValue.slice(0, nextCaretChars) +
        filteredValue.slice(nextCaretChars + 1)
      : filteredValue;

    if (thousandSeparator) placeCaret(nextCaretChars);
    onChange(nextValue === '-' ? '' : nextValue);
  };

  const handleBeforeInput = (e: InputEvent<HTMLInputElement>) => {
    const char = e.data;
    if (!(e.target instanceof HTMLInputElement) || !char) return;

    const isAtStart = e.target.selectionStart === 0;
    if (
      !/^[0-9-]$/.test(char) ||
      (char === '-' && (!allowNegative || !isAtStart || value.includes('-')))
    ) {
      e.preventDefault();
    }
  };

  // A selection of separators alone holds nothing to delete: the key only
  // moves the caret, so no digit beside them goes.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const input = inputRef.current;
    if (
      !thousandSeparator ||
      !input ||
      (e.key !== 'Backspace' && e.key !== 'Delete')
    ) {
      return;
    }
    const { selectionStart: start, selectionEnd: end } = input;
    if (
      start === null ||
      end === null ||
      start === end ||
      !/^,+$/.test(input.value.slice(start, end))
    ) {
      return;
    }
    e.preventDefault();
    const caret = e.key === 'Backspace' ? start : end;
    input.setSelectionRange(caret, caret);
  };

  return (
    <Input
      ref={(node) => {
        inputRef.current = node;
      }}
      type="text"
      inputMode="numeric"
      pattern={pattern}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      onBeforeInput={handleBeforeInput}
      onKeyDown={handleKeyDown}
      min={min}
      max={max}
      width={width}
      disabled={disabled}
      message={message}
      isError={isError}
      required={required}
      label={label}
      aria-label={ariaLabel}
    />
  );
};
